import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import s from './index.module.scss';

interface ReadMoreProps {
  html: string;
  rows?: number;
  collapsedHeight?: number;
  className?: string;
  viewMoreLabel?: string;
  hideLabel?: string;
  scrollToElement?: () => void;
}

export const ReadMore: React.FC<ReadMoreProps> = ({
  html,
  rows = 3,
  collapsedHeight,
  className,
  viewMoreLabel,
  hideLabel,
  scrollToElement,
}) => {
  const { t } = useAppTranslation('common');

  const [expanded, setExpanded] = useState(false);
  const [exceeds, setExceeds] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const defaultMaxHeight = rows * 20;
  const maxHeight = collapsedHeight || defaultMaxHeight;

  const viewLabel = viewMoreLabel || t('View more');
  const hideText = hideLabel || t('Hide');

  useEffect(() => {
    if (contentRef.current && !expanded) {
      const fullHeight = contentRef.current.scrollHeight;

      setExceeds(fullHeight > maxHeight + 5);
    }
  }, [html, rows, collapsedHeight, expanded]);

  const handleToggle = (): void => {
    if (expanded && contentRef.current && scrollToElement) {
      scrollToElement();
    }

    setExpanded(!expanded);
  };

  return (
    <div className={classNames(className, 'my-3')}>
      <div
        ref={contentRef}
        dangerouslySetInnerHTML={{ __html: html }}
        className={classNames(s.content, {
          [s.expanded]: expanded,
        })}
        style={{
          ['--collapsed-height' as any]: `${maxHeight}px`,
        }}
      />
      {exceeds && (
        <div onClick={handleToggle} className="mt-2 text-accent cursor-pointer">
          {expanded ? hideText : viewLabel}
        </div>
      )}
    </div>
  );
};
